/* eslint-disable no-void */

import * as vp from 'vopay';

import { useEffect, useState } from 'react';
import axios from 'axios';

import vopayApi from '../../services/api.config';
import { useDataFetchIndicator } from '..';
import { useCache } from '../../providers/Cache.provider';

type HookProps = {
  api: vp.Api;
};

export type PartnerInvoiceDetailsRequestParams = {
  vopayAccountId?: string;
  year: string;
  month: string;
};

export type PartnerInvoiceDetailsResponse = {
  success: boolean;
  errorMessage: string;
  invoiceDetails: vp.PartnerInvoiceDetailsInvoiceDetails;
  invoicePeriod: vp.PartnerInvoiceDetailsInvoicePeriod;
};

type ReturnTuple = [
  PartnerInvoiceDetailsResponse | undefined,
  (requestParams: PartnerInvoiceDetailsRequestParams, options?: { useCache: boolean }) => void,
  () => void,
];

function useFetchPartnerInvoiceDetails({ api }: HookProps): ReturnTuple {
  const source = axios.CancelToken.source();

  const [params, setParams] = useState<PartnerInvoiceDetailsRequestParams>();
  const [response, setResponse] = useState<PartnerInvoiceDetailsResponse>();
  const [shouldSendRequest, setSendRequest] = useState(false);
  const [, setDataFetchingInfo] = useDataFetchIndicator();
  const { cache } = useCache();

  useEffect(() => {
    const sendRequest = async (requestParams: PartnerInvoiceDetailsRequestParams) => {
      const [getInvoiceDetails, cacheInvoiceDetails] = cache<PartnerInvoiceDetailsResponse>(
        `invoice-details.${requestParams.year}.${requestParams.month}`,
      );

      const cachedValue = getInvoiceDetails();

      if (cachedValue) {
        setResponse(cachedValue);
        setSendRequest(false);

        return () => {};
      }

      setDataFetchingInfo(true, 'loading invoice details', 'fullScreen');

      const { Success, ErrorMessage, InvoiceDetails, InvoicePeriod } = await api.fetchPartnerInvoiceDetails(
        buildParams(requestParams),
        source,
      );

      const res = {
        success: Success,
        errorMessage: ErrorMessage,
        invoiceDetails: InvoiceDetails,
        invoicePeriod: InvoicePeriod,
      };

      cacheInvoiceDetails(res);
      setResponse(res);
      setSendRequest(false);
      setDataFetchingInfo(false);

      return () => {};
    };

    if (params && shouldSendRequest) void sendRequest(params);

    return () => {
      source.cancel();
    };
  }, [shouldSendRequest]);

  return [
    response,
    (requestParams: PartnerInvoiceDetailsRequestParams) => {
      setParams(requestParams);
      setSendRequest(true);
    },
    () => {
      setParams(undefined);
      setResponse(undefined);
      setSendRequest(false);
    },
  ];

  function buildParams({ vopayAccountId, year, month }: PartnerInvoiceDetailsRequestParams) {
    return {
      VopayAccountID: vopayAccountId,
      Year: year,
      Month: month,
    };
  }
}

export default vopayApi.withApiHook<HookProps, ReturnTuple>(useFetchPartnerInvoiceDetails);
